import React, { useMemo } from 'react';
import { JoykitIcon } from '@components/JoykitIcon';
import {
  LanguageLink,
  ModalBody,
  ModalHeader,
  StyledButton,
  Card,
  CloseButton,
  CloseIcon,
  CardInnerContainer,
  ModalContent,
  ModalOverlay,
  HiddenLanguageLinksContainer,
  HiddenLanguageLink,
} from './LanguageMenu.styles';
import { useTranslation } from 'react-i18next';
import { StyledModal } from '@components/StyledModal';
import { withWindow } from '@utils/withWindow';
import { updateUrlQueryArg } from '@utils/queryString';
import { languages } from '@i18n/languages';

const analytics = require('/static/js/joy');

const selectableLanguages = new Map(languages.map((lang) => [lang.languageCode, lang.name]));

export const LanguageMenu = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  const { t, i18n } = useTranslation('languageMenu');

  const languageCode = i18n.language;

  const currentUrl = withWindow((global) => global.document.location.href, 'https://withjoy.com');

  const { languageLinks, hiddenLinks } = useMemo(() => {
    const languageLinks = [];
    const hiddenLinks = [];

    for (const [code, name] of selectableLanguages.entries()) {
      const href = updateUrlQueryArg(currentUrl, 'l', code);

      languageLinks.push(
        <LanguageLink
          id={`${code}LanguageLink`}
          key={code}
          href={href}
          hrefLang={code}
          isSelected={code === languageCode}
          onClick={async () => {
            analytics.segment.sendTelemetryEvent('MarcomLanguageSelected', {
              extraInfo: {
                fromLanguage: languageCode,
                toLanguage: code,
              },
            });
            setIsOpen(false);
          }}
        >
          {name}
        </LanguageLink>
      );

      hiddenLinks.push(
        <HiddenLanguageLink key={`hidden-${code}`} href={href} hrefLang={code} rel="alternate" aria-label={`Change language to ${name}`}>
          {name}
        </HiddenLanguageLink>
      );
    }
    return { languageLinks, hiddenLinks };
  }, [currentUrl, languageCode]);

  return (
    <>
      {/* Hidden language links for SEO - accessible to crawlers and screen readers but visually hidden */}
      <HiddenLanguageLinksContainer>
        <nav aria-label="Change Language">{hiddenLinks}</nav>
      </HiddenLanguageLinksContainer>

      <StyledModal
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={false}
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        styledContent={ModalContent}
        styledOverlay={ModalOverlay}
        closeTimeoutMS={500}
      >
        <Card>
          <CloseButton id={'close'} onClick={() => setIsOpen(false)}>
            <CloseIcon name="notAttending" color="mono1" />
          </CloseButton>
          <CardInnerContainer>
            <ModalHeader>{t('title')}</ModalHeader>
            <ModalBody>{languageLinks}</ModalBody>
          </CardInnerContainer>
        </Card>
      </StyledModal>
      <StyledButton onClick={() => setIsOpen(!isOpen)}>
        <JoykitIcon name="globe" size={16} />
        <span>{selectableLanguages.get(languageCode)}</span>
      </StyledButton>
    </>
  );
};
